/* =====================================================
   Damlab — Page Chrome (shared header/navigation modules)
   ----------------------------------------------------
   Modules that drive the topbar / navbar / mobile nav /
   language dropdown — chrome that appears on every page.
   Routed to '*' so they mount everywhere.

   The hero-driven "show sticky navbar when hero exits"
   behavior stays in home.js (it's homepage-only — other
   pages don't have a hero, the sticky navbar is always
   visible by default CSS).
   ===================================================== */
(function (App) {
  'use strict';
  if (!App) { console.error('[chrome] App core not loaded'); return; }

  // ── chrome.mobileNav ────────────────────────────────
  // Uses document-level event delegation (same pattern as cart-drawer.js)
  // so the toggle works regardless of when the DOM was rendered, even if
  // sections are re-rendered by Shopify's Section Rendering API.
  App.register('chrome.mobileNav', {
    mount(ctx) {
      // ── DOM-state-only mobile nav ─────────────────────
      // Previous versions kept a JS `activeMenu` variable as the source of
      // truth. That variable could drift from the actual DOM (after fast
      // taps, section re-renders, or any external code touching classes),
      // causing openInline to either skip ("guard thinks something is
      // already open") or open into a navbar still wearing leftover
      // .nav-open (transparent-navbar treatment from a prior fullscreen
      // session), making the inline menu look broken.
      //
      // This rewrite uses ONLY the DOM as state — every check reads
      // .classList from the live elements. No variable drift possible.

      function elements() {
        return {
          fs:           document.getElementById('mobileFsNav'),
          inline:       document.getElementById('mobileNavSticky'),
          heroBurger:   document.getElementById('hamburger'),
          stickyBurger: document.getElementById('hamburgerSticky'),
          heroNavbar:   document.querySelector('.hero__navbar'),
          navbar:       document.getElementById('navbarSticky')
        };
      }

      function isFsOpen()     { return !!document.getElementById('mobileFsNav')?.classList.contains('open'); }
      function isInlineOpen() { return !!document.getElementById('mobileNavSticky')?.classList.contains('open'); }
      function isOpen()       { return isFsOpen() || isInlineOpen(); }
      function activeType()   { return isFsOpen() ? 'fullscreen' : (isInlineOpen() ? 'inline' : null); }

      function clearFullscreenState() {
        const e = elements();
        e.fs?.classList.remove('open');
        e.fs?.setAttribute('aria-hidden', 'true');
        e.heroBurger?.classList.remove('open');
        e.heroBurger?.setAttribute('aria-expanded', 'false');
        if (e.heroNavbar) {
          e.heroNavbar.style.zIndex = '';
          // Restore base position (CSS sets .hero__navbar to position:absolute
          // — we override to fixed while the fullscreen menu is open so the
          // logo + X button stay viewport-anchored even if the page scrolled
          // before the menu opened).
          e.heroNavbar.style.position = '';
          e.heroNavbar.style.top = '';
          e.heroNavbar.style.left = '';
          e.heroNavbar.style.right = '';
        }
        if (e.navbar) {
          e.navbar.style.zIndex = '';
          // Critical: leftover .nav-open kills the navbar background, which
          // is exactly what makes the inline menu look invisible when it
          // opens next.
          e.navbar.classList.remove('nav-open');
        }
      }

      function clearInlineState() {
        const e = elements();
        if (e.inline) {
          e.inline.classList.remove('open');
          e.inline.setAttribute('aria-hidden', 'true');
          // Also clear inline-style fallbacks (set by openInline below)
          e.inline.style.maxHeight = '';
          e.inline.style.padding = '';
        }
        e.stickyBurger?.classList.remove('open');
        e.stickyBurger?.setAttribute('aria-expanded', 'false');
      }

      function openFullscreen() {
        const e = elements();
        if (!e.fs) {
          console.info('[mobileNav:fullscreen]', { result: 'no-fs-element', activeMenu: activeType() });
          return;
        }
        clearInlineState();   // defensive: tear down any inline leftovers

        e.fs.classList.add('open');
        e.fs.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        e.heroBurger?.classList.add('open');
        e.heroBurger?.setAttribute('aria-expanded', 'true');
        if (e.heroNavbar) {
          e.heroNavbar.style.zIndex = '4001';
          // Pin to viewport so logo + X stay visible even if the user
          // scrolled before opening the menu. .hero__navbar is normally
          // position:absolute (anchored to hero element). Without this
          // override, scrolling the hero out of view drags the navbar
          // with it, leaving the menu visually orphaned with no logo
          // and no close button. Restored on closeFullscreen().
          e.heroNavbar.style.position = 'fixed';
          e.heroNavbar.style.top = '0';
          e.heroNavbar.style.left = '0';
          e.heroNavbar.style.right = '0';
        }
        if (e.navbar) {
          e.navbar.style.zIndex = '4001';
          e.navbar.classList.add('nav-open');
        }
        const cs = getComputedStyle(e.fs);
        console.info('[mobileNav:fullscreen]', {
          result: 'opened', activeMenu: activeType(),
          hasFs: !!e.fs, hasInline: !!e.inline,
          fsOpacity: cs.opacity, fsVisibility: cs.visibility
        });
        App.bus.emit('mobileNav:opened', { type: 'fullscreen' });
      }

      function openInline() {
        const e = elements();
        if (!e.inline) {
          console.info('[mobileNav:inline]', { result: 'no-inline-element', activeMenu: activeType() });
          return;
        }
        clearFullscreenState();   // defensive: tear down any fullscreen leftovers

        e.inline.classList.add('open');
        e.inline.setAttribute('aria-hidden', 'false');
        // BELT-AND-SUSPENDERS: also set the visibility-critical styles inline.
        // If .mobile-nav.open class isn't winning the cascade for any reason
        // (CSS load order, !important rule, cached stylesheet), these
        // inline styles guarantee the menu becomes visible.
        e.inline.style.maxHeight = '500px';
        e.inline.style.padding = '20px clamp(20px, 5vw, 40px)';

        e.stickyBurger?.classList.add('open');
        e.stickyBurger?.setAttribute('aria-expanded', 'true');
        // Force sticky navbar visible (matters on homepage if home.navbar's
        // IntersectionObserver hasn't fired yet — otherwise navbar is at
        // translateY(-100%) and the inline menu inside is off-screen).
        if (e.navbar) e.navbar.classList.add('visible');

        // NOTE: intentionally NOT locking body scroll for the inline menu.
        // The inline menu is a dropdown — locking scroll causes iOS Safari
        // to jump the viewport to top, making the menu appear "not to
        // open". Inline menu opens beneath the navbar; user can scroll.

        const cs = getComputedStyle(e.inline);
        console.info('[mobileNav:inline]', {
          result: 'opened',
          clicked: e.stickyBurger?.id || '(programmatic)',
          activeMenu: activeType(),
          hasInline: !!e.inline,
          hasFs: !!e.fs,
          inlineClasses: e.inline.className,
          navbarClasses: e.navbar?.className,
          navbarVisible: e.navbar?.classList.contains('visible') ?? false,
          navbarHasNavOpen: e.navbar?.classList.contains('nav-open') ?? false,
          computedMaxHeight: cs.maxHeight,
          computedVisibility: cs.visibility,
          computedDisplay: cs.display,
          computedOverflow: cs.overflow
        });
        App.bus.emit('mobileNav:opened', { type: 'inline' });
      }

      function closeFullscreen() {
        clearFullscreenState();
        document.body.style.overflow = '';
        App.bus.emit('mobileNav:closed', { type: 'fullscreen' });
      }
      function closeInline() {
        clearInlineState();
        // No body.overflow to restore — openInline doesn't lock it.
        App.bus.emit('mobileNav:closed', { type: 'inline' });
      }
      function close() {
        const t = activeType();
        if (t === 'fullscreen')      closeFullscreen();
        else if (t === 'inline')     closeInline();
        // Defensive cleanup even if neither was open (cleans drift state).
        clearFullscreenState();
        clearInlineState();
        document.body.style.overflow = '';
      }

      // ── Document-delegated click. Routes by which hamburger fired.
      ctx.on(document, 'click', (e) => {
        const burger = e.target.closest('.hamburger, [data-mobile-nav-toggle]');
        if (burger) {
          e.preventDefault();
          const isHero = burger.id === 'hamburger' || !!burger.closest('.hero__navbar');
          const open = isOpen();
          console.info('[mobileNav:click]', {
            clicked: burger.id || burger.className,
            isHero,
            isOpenBeforeClick: open,
            activeMenu: activeType(),
            hasFs: !!document.getElementById('mobileFsNav'),
            hasInline: !!document.getElementById('mobileNavSticky'),
            navbarVisible: document.getElementById('navbarSticky')?.classList.contains('visible') ?? false
          });
          if (open) { close(); return; }
          if (isHero) openFullscreen();
          else        openInline();
          return;
        }
        // Click any link inside an open menu → close
        const linkInMenu = e.target.closest('#mobileFsNav a, #mobileNavSticky a');
        if (linkInMenu && isOpen()) close();
      });

      ctx.on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) close();
      });

      // Console-accessible debug helper
      App.mobileNav = {
        openFullscreen, openInline, close, isOpen,
        get active()  { return activeType(); },
        get isFsOpen()     { return isFsOpen(); },
        get isInlineOpen() { return isInlineOpen(); },
        elements
      };
    }
  });

  // ── chrome.loader ───────────────────────────────────
  // Dismisses the full-viewport `#loading-screen` overlay on any
  // page that ships one. The homepage uses `home.loader` for its
  // hero-boxing transition; this module skips when `.hero` is
  // present so the two never fight.
  App.register('chrome.loader', {
    mount(ctx) {
      const loader = document.getElementById('loading-screen');
      if (!loader) return;
      if (document.querySelector('.hero')) return; // home.loader owns the hero flow

      // LOADER-ONCE: already seen this session → drop the DOM and bail.
      try {
        if (sessionStorage.getItem('damlab.loader.seen') === '1') {
          loader.remove();
          return;
        }
      } catch (_) {}

      const MAX_VISIBLE_MS = 2000;
      let dismissed = false;
      function dismiss() {
        if (dismissed) return; dismissed = true;
        try { sessionStorage.setItem('damlab.loader.seen', '1'); } catch (_) {}
        loader.classList.add('loader--dismiss');
        setTimeout(() => { loader.remove(); }, 600);
      }

      // Hard cap — never visible longer than MAX_VISIBLE_MS even if window.load is slow.
      setTimeout(dismiss, MAX_VISIBLE_MS);
      if (document.readyState === 'complete') dismiss();
      else ctx.on(window, 'load', dismiss);
    }
  });

  App.route('*', ['chrome.mobileNav', 'chrome.loader']);

})(window.App);
